import { useCallback, useMemo, useState } from "react";
import { previewPubmedIngestQuery, type PubmedQueryPreview } from "../lib/corpusApi";
import { findQuerySyntaxProblem } from "../lib/pubmed_query_syntax";
import { useAsyncHandler } from "./useAsyncHandler";

export interface UsePubmedQueryValidationOptions {
  /** Whether the extraction service that answers a validation is up. Until it is, there is nothing
   *  to validate against, so Validate stays unavailable. */
  extractionWarm: boolean;
  /** How many of the matching articles to describe, by title and link. Default none. */
  sampleSize?: number;
}

export interface UsePubmedQueryValidationResult {
  query: string;
  setQuery: (query: string) => void;
  /** The query without the whitespace around it, which is what is validated and what is kept. */
  trimmedQuery: string;
  /** A structural mistake in the typed query that NCBI would not report itself, or null. */
  syntaxProblem: string | null;
  /** Whether Validate can be pressed: the extraction service is up, the query has no structural
   *  mistake, and no check is running. */
  canValidate: boolean;
  validate: () => void;
  validating: boolean;
  /** NCBI's refusal of the query, or the check's failure. Null once the query has been edited. */
  error: string | null;
  /** What PMC said about the query as it now stands; null until it is validated, and again once
   *  the query is edited. */
  preview: PubmedQueryPreview | null;
}

/**
 * A PubMed Central query and its validation: sent to PMC, through the extraction service, for its
 * match count and a sample of matches. Editing the query undoes the validation, so whatever is
 * built from the query is built from one whose preview was just seen. An edit that only changes the
 * whitespace around it does not count.
 */
export function usePubmedQueryValidation({
  extractionWarm,
  sampleSize = 0,
}: UsePubmedQueryValidationOptions): UsePubmedQueryValidationResult {
  const [query, setQuery] = useState("");
  const [validated, setValidated] = useState<{
    query: string;
    preview: PubmedQueryPreview;
  } | null>(null);
  const [attemptedQuery, setAttemptedQuery] = useState<string | null>(null);

  const trimmedQuery = query.trim();
  const syntaxProblem = useMemo(
    () => (trimmedQuery === "" ? null : findQuerySyntaxProblem(trimmedQuery)),
    [trimmedQuery]
  );

  const check = useAsyncHandler(async () => {
    setAttemptedQuery(trimmedQuery);
    setValidated({
      query: trimmedQuery,
      preview: await previewPubmedIngestQuery(trimmedQuery, sampleSize),
    });
  });

  const { handle: runCheck } = check;
  const validate = useCallback(() => void runCheck(), [runCheck]);

  return {
    query,
    setQuery,
    trimmedQuery,
    syntaxProblem,
    canValidate: extractionWarm && trimmedQuery !== "" && syntaxProblem === null && !check.loading,
    validate,
    validating: check.loading,
    error: attemptedQuery === trimmedQuery ? check.error : null,
    preview: validated?.query === trimmedQuery ? validated.preview : null,
  };
}
